import { datadogRum } from '@datadog/browser-rum';
import { t } from 'i18next';
import { jwtDecode } from 'jwt-decode';
import type { KyInstance } from 'ky';
import ky, { HTTPError, NetworkError, TimeoutError } from 'ky';
import { TempoApiError, tempoApiErrorWrapperSchema } from '@/common/api/tempoApiTypes';
import { redactSensitiveParams } from '@/common/monitoring/datadogRum';
import { getBriefErrorMessage } from '@/common/utils/getBriefErrorMessage';
import { getEnv } from '@/common/utils/getEnv';

function createTokenManager() {
  let getTokenFn: (() => Promise<string | null>) | null = null;
  let logoutFn: (() => void) | null = null;
  let cachedToken: string | null = null;
  let tokenExpirySeconds = 0;
  const clearToken = () => {
    cachedToken = null;
    tokenExpirySeconds = 0;
  };
  return {
    setTokenProvider(provider: () => Promise<string | null>) {
      getTokenFn = provider;
    },

    setLogoutHandler(handler: () => void) {
      logoutFn = handler;
    },

    setToken(token?: string) {
      cachedToken = token ?? null;
    },

    async getToken(forceRefresh = false): Promise<string | null> {
      if (!getTokenFn) {
        return null;
      }
      const nowInSeconds = Math.floor(Date.now() / 1000);
      if (!forceRefresh && cachedToken && nowInSeconds < tokenExpirySeconds) {
        return cachedToken;
      }

      try {
        cachedToken = await getTokenFn();
        if (!cachedToken) {
          clearToken();
          return null;
        }
        const leeway = 2 * 60; // 2 minutes in seconds
        const decodedToken = jwtDecode<{ exp: number }>(cachedToken);
        tokenExpirySeconds = decodedToken.exp - leeway;
        return cachedToken;
      } catch (error) {
        clearToken();
        if ((error as { error?: string }).error === 'login_required') {
          logoutFn?.();
        }
        return null;
      }
    },
    clearToken,
    logout() {
      logoutFn?.();
    },
  };
}

function tokenHasExpectedAudience(token: string): boolean {
  try {
    const { aud } = jwtDecode<{ aud?: string | string[] }>(token);
    const expected = getEnv().loopAudience;
    return Array.isArray(aud) ? aud.includes(expected) : aud === expected;
  } catch {
    return false;
  }
}

const tokenManager = createTokenManager();

export const setTokenProvider = (provider: () => Promise<string | null>, token?: string) => {
  tokenManager.setTokenProvider(provider);
  tokenManager.setToken(token);
};

export const setLogoutHandler = (handler: () => void) => {
  tokenManager.setLogoutHandler(handler);
};

export const getAuthToken = (): Promise<string | null> => tokenManager.getToken();

export const tempoApi = ({ usePrefix = true }: { usePrefix?: boolean } = {}): KyInstance => {
  const prefixUrl = getEnv().apiBaseUrl;
  return ky.create({
    prefix: usePrefix ? prefixUrl : '',
    hooks: {
      beforeRequest: [
        async ({ request }) => {
          const token = await tokenManager.getToken();
          if (token) {
            request.headers.set('Authorization', `Bearer ${token}`);
          }
        },
      ],
      beforeError: [
        ({ request, error }) => {
          if (error instanceof TempoApiError) {
            return error;
          }

          const isServerError = error instanceof HTTPError && error.response.status >= 500;
          const isServerOrConnectivityError =
            isServerError || error instanceof TimeoutError || error instanceof NetworkError;

          if (isServerError) {
            error.message = redactSensitiveParams(error.message);
            datadogRum.addError(error, {
              source: 'api-client',
              method: request.method,
              url: redactSensitiveParams(request.url),
              status: error.response.status,
              technicalMessage: error.message,
            });
          }

          error.message = isServerOrConnectivityError
            ? t(($) => $.common.errors.genericWithSupport)
            : getBriefErrorMessage(request.method, new URL(request.url).pathname);
          return error;
        },
      ],
      afterResponse: [
        async ({ request, response }) => {
          if (response.ok) return response;

          if (response.status === 401) {
            const newToken = await tokenManager.getToken(true);

            if (newToken) {
              if (!tokenHasExpectedAudience(newToken)) {
                tokenManager.logout();
                return response;
              }
              const newRequest = request.clone();
              newRequest.headers.set('Authorization', `Bearer ${newToken}`);
              return ky(newRequest);
            }
          }

          if (response.status === 400 || response.status === 409) {
            let raw: unknown;
            try {
              raw = await response.clone().json();
            } catch {
              throw new Error('Non-JSON error response');
            }

            const rawWithStatus = { ...(raw as Record<string, unknown>), status: response.status };
            const parsed = tempoApiErrorWrapperSchema.parse(rawWithStatus);

            throw new TempoApiError(parsed, response, request);
          }

          return response;
        },
      ],
    },
  });
};
